
import { useState } from "react";
import { Invoice } from "@/types/payments";
import { getClosestBusinessDayBefore, isBankingHoliday, isAdjustedForHoliday } from "@/utils/dateUtils";

export function useInvoiceTableHelpers() {
  // Get today's date for the disabled dates function
  const today = new Date();
  today.setHours(0, 0, 0, 0); // Reset to start of day for accurate comparison

  // Function to disable dates before today in the calendar
  const disablePastDates = (date: Date) => {
    const dateToCheck = new Date(date);
    dateToCheck.setHours(0, 0, 0, 0);
    return dateToCheck < today;
  };

  // Helper to safely create a Date object from payment date string
  const getPaymentDateObject = (invoice: Invoice) => {
    if (!invoice.paymentDate) {
      // If there's no payment date, set it based on the due date
      // Check if due date is a banking holiday
      const dueDate = new Date(invoice.dueDate);
      if (isBankingHoliday(dueDate)) {
        // If due date is a holiday, set payment date to closest business day before
        return getClosestBusinessDayBefore(dueDate);
      }
      return new Date(invoice.dueDate);
    }
    return new Date(invoice.paymentDate);
  };

  // Check if payment date is after due date
  const isLatePayment = (dueDate: string, paymentDate: string): boolean => {
    return new Date(paymentDate) > new Date(dueDate);
  };

  // Check if the payment date has been automatically adjusted for a banking holiday
  const isHolidayAdjusted = (invoice: Invoice): boolean => {
    const dueDate = new Date(invoice.dueDate);
    
    // If payment date is not set or is equal to due date, we're using the default calculation
    if (!invoice.paymentDate || invoice.paymentDate === invoice.dueDate) {
      return isBankingHoliday(dueDate);
    }
    
    // If the payment date is different from the due date but the due date is not a holiday,
    // then it's likely a manual selection - don't show "Adjusted for holiday" text
    if (!isBankingHoliday(dueDate)) {
      return false;
    }
    
    // If due date is a banking holiday, check if payment date is the adjusted date
    const paymentDateObj = new Date(invoice.paymentDate);
    const expectedAdjusted = getClosestBusinessDayBefore(dueDate);
    
    // Only show "Adjusted for holiday" if the payment date matches what our adjustment logic would produce
    return expectedAdjusted.getTime() === paymentDateObj.getTime();
  };

  // Get the payment date with holiday adjustment if needed
  const getAdjustedPaymentDate = (dueDate: string): Date => {
    const dueDateObject = new Date(dueDate);
    return isBankingHoliday(dueDateObject) 
      ? getClosestBusinessDayBefore(dueDateObject)
      : dueDateObject;
  };

  return {
    disablePastDates,
    getPaymentDateObject,
    isLatePayment,
    isHolidayAdjusted,
    getAdjustedPaymentDate,
    isBankingHoliday
  };
}
